import type React from "react";

import dayjs from "dayjs";

import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { OverflowTooltip } from "app/pages/sales-intelligence/common-components/OverflowTooltip/OverflowTooltip";
import {
    StyledLoadingSpinner,
    StyledLoadingTag,
    StyledRemoveIconButton,
} from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/FileUpload/styles";
import { useDeleteFile } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/queries/useDeleteFile";
import type { KBFile } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/types";

type Props = {
    file: KBFile;
    onDeleteSuccess: () => void;
    dataAutomation?: string;
};

export const FileItem: React.FC<Props> = ({ file, onDeleteSuccess, dataAutomation }) => {
    const { mutate, isPending } = useDeleteFile();

    const isFilePending = file.isProcessing;

    const handleRemove = () => {
        mutate(file.id, {
            onSuccess: onDeleteSuccess,
        });
    };

    const formattedDate = dayjs(file.createdDate).format("DD/MM/YY");

    return (
        <Flex
            padding="8px 0 8px 8px"
            alignItems="center"
            borderBottom={`1px solid ${colorsPalettes.carbon[50]}`}
            data-automation={dataAutomation}
        >
            <Flex
                maxWidth="345px"
                alignItems="center"
                gap="8px"
            >
                <OverflowTooltip
                    text={file.name}
                    fontVariant="body2"
                    color={colorsPalettes.carbon[400]}
                    dataAutomation={`${dataAutomation}-name`}
                />
                {isFilePending ? (
                    <StyledLoadingTag>
                        <StyledLoadingSpinner color={colorsPalettes.orange[300]} />
                        <Text
                            fontVariant="caption"
                            color={colorsPalettes.orange[300]}
                        >
                            {translate("si.agent_knowledge_base.modal.file.loading.text")}
                        </Text>
                    </StyledLoadingTag>
                ) : null}
            </Flex>
            <Text
                fontVariant="body2"
                color={colorsPalettes.carbon[300]}
                display="block"
                margin="0 16px 0 auto"
                data-automation={`${dataAutomation}-date`}
            >
                {formattedDate}
            </Text>
            <Tooltip content={translate("common.delete")}>
                <StyledRemoveIconButton
                    iconName="delete"
                    iconColor={colorsPalettes.carbon[300]}
                    iconSize="18px"
                    type="flat"
                    onClick={handleRemove}
                    isDisabled={isPending}
                    data-automation={`${dataAutomation}-button`}
                />
            </Tooltip>
        </Flex>
    );
};
